import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import useAuth from "@/hooks/useAuth";
import placesApi from "@/api/placesApi";
import PlaceResponse from "@/model/PlaceResponse";
import "@/pages/PlacesPage/PlacesPage.scss";

const PAGE_SIZE = 20;

function PlacesPage() {
  const { t } = useTranslation();
  const { session } = useAuth();

  const [places, setPlaces] = useState<PlaceResponse[]>([]);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);

  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState(false);

  useEffect(() => {
    if (!session) {
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    setError(false);

    placesApi
      .listPlaces(session.accessToken, page, PAGE_SIZE)
      .then((result) => {
        if (cancelled) {
          return;
        }
        setPlaces(result.content ?? []);
        setTotalPages(result.totalPages ?? 0);
      })
      .catch(() => {
        if (!cancelled) {
          setError(true);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [session, page]);

  if (!session) {
    return null;
  }

  const handleDelete = async (id: string) => {
    if (!window.confirm(t("places.deleteConfirm"))) {
      return;
    }

    setDeletingId(id);
    setDeleteError(false);

    try {
      await placesApi.deletePlace(session.accessToken, id);
      setPlaces((current) => current.filter((place) => place.id !== id));
    } catch {
      setDeleteError(true);
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="places-page">
      <header className="places-page__header">
        <div>
          <h1>{t("places.title")}</h1>
          <p className="places-page__subtitle">{t("places.subtitle")}</p>
        </div>
        <Link to="/places/new">{t("places.createButton")}</Link>
      </header>

      {deleteError && (
        <p className="places-page__error">{t("places.deleteError")}</p>
      )}

      {isLoading && <p>{t("places.loading")}</p>}
      {!isLoading && error && <p role="alert">{t("places.error")}</p>}
      {!isLoading && !error && places.length === 0 && <p>{t("places.empty")}</p>}

      {!isLoading && !error && places.length > 0 && (
        <>
          <table className="places-page__table">
            <thead>
              <tr>
                <th>{t("places.columns.name")}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {places.map((place, index) => (
                <tr key={place.id ?? index}>
                  <td>{place.name ?? "—"}</td>
                  <td className="places-page__actions">
                    {place.id && (
                      <>
                        <Link to={`/places/${place.id}/edit`}>
                          {t("places.editButton")}
                        </Link>
                        <button
                          type="button"
                          onClick={() => handleDelete(place.id as string)}
                          disabled={deletingId === place.id}
                        >
                          {deletingId === place.id
                            ? t("places.deleting")
                            : t("places.deleteButton")}
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="places-page__pagination">
            <button
              type="button"
              onClick={() => setPage((current) => current - 1)}
              disabled={page <= 0}
            >
              {t("pagination.previous")}
            </button>
            <span>
              {t("pagination.pageInfo", { page: page + 1, totalPages })}
            </span>
            <button
              type="button"
              onClick={() => setPage((current) => current + 1)}
              disabled={page + 1 >= totalPages}
            >
              {t("pagination.next")}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

export default PlacesPage;
